import React, { memo, useState, useEffect } from 'react';
import { Button, Flex, Text, Collapse, FlexColumn, Callout, Tooltip, Popover, Spinner, Icon } from 'core/components';
import { TextArea } from 'core/form';
import CopyToClipboard from 'app/components/CopyToClipboardButton';
import { BsTerminal } from 'react-icons/bs';

import { FiCopy, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import { TbLayoutSidebar } from 'react-icons/tb';
import Markdown from '../../journeys/Markdown';
import {
  ReasoningDetails,
  ReasoningContent,
  StepContainer,
  SubAgentContainer,
  ToolCallCard
} from '../KaizenChatSession.styles';
import KaizenToolResult from '../KaizenToolResult';
import FeedbackModel from '../models/FeedbackModel';
import { getAgentDisplayName } from '../util/agentDisplayNames';

// Sub-agent group — renders a single lightweight summary entry in the main thread.
// The sub-agent's reasoning and tool calls are NOT shown inline; clicking the entry opens
// the full transcript in the right-hand tool results panel.
const SubAgentGroup = memo(({ agentName, toolCalls, step, reasoning, onSelectToolResult }) => {
  const completedCount = toolCalls.filter((tc) => tc.status === 'SUCCESS').length;
  const totalCount = toolCalls.length;
  const isProcessing = toolCalls.some((tc) => !tc.status || tc.status === 'PROCESSING' || tc.status === 'PENDING');

  const statusText = (() => {
    if (isProcessing) {
      return `Running (${completedCount}/${totalCount} tools...)`;
    }
    if (completedCount === totalCount) {
      return `${totalCount} tool ${totalCount === 1 ? 'call' : 'calls'} completed`;
    }
    return `${completedCount}/${totalCount} tool calls completed`;
  })();

  const handleOpenTranscript = () => {
    onSelectToolResult({
      kind: 'subagent-transcript',
      id: `subagent-transcript-${step.id || step.step_number || 'step'}-${agentName}`,
      agentName,
      reasoning,
      toolCalls,
      title: `${getAgentDisplayName(agentName)} details`
    });
  };

  return (
    <SubAgentContainer>
      <ToolCallCard interactive elevation={0} onClick={handleOpenTranscript}>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap={1}>
            {isProcessing ? <Spinner size={16} /> : <Icon icon={BsTerminal} color="success" size={14} />}
            <Flex flexDirection="column" gap="2px">
              <Text fontWeight={500}>{getAgentDisplayName(agentName)}</Text>
              <Text small muted>
                {statusText}
              </Text>
            </Flex>
          </Flex>
          <Flex alignItems="center" gap="6px">
            <Text muted small>
              View details
            </Text>
            <Icon icon={TbLayoutSidebar} size={14} color="muted" />
          </Flex>
        </Flex>
      </ToolCallCard>
    </SubAgentContainer>
  );
});

SubAgentGroup.displayName = 'SubAgentGroup';

// Memoized step component to prevent unnecessary re-renders
const StepComponent = memo(
  ({
    step,
    stepIndex,
    turn,
    $auth,
    isStepReasoningOpen,
    onToggleReasoning,
    onSelectToolResult,
    onSendToExplorer,
    isCopilotMode,
    isExistingChatLoading,
    sessionModel
  }) => {
    const stepId = step.id || stepIndex;
    const { llm_response = {}, toolCalls } = step;

    const hasFinalAnswer =
      llm_response?.final_answer && step.processing_status === 'COMPLETED' && step.parent_step_id === null;
    const hasSubAgentCalls = toolCalls?.some((tc) => tc.agent_name && tc.agent_name !== 'kaizen');

    const [showThankYouMessage, setShowThankYouMessage] = useState(false);
    const [showFeedbackPopover, setShowFeedbackPopover] = useState(false);

    // Memoize feedbackModel so it's only created when dependencies change
    const feedbackModel = React.useMemo(() => {
      if (hasFinalAnswer) {
        const currentUserFeedbackRecord = turn.feedback?.find(
          (item) => String(item.user_id) === String($auth.activeUser.id)
        );
        if (currentUserFeedbackRecord) {
          return new FeedbackModel(currentUserFeedbackRecord);
        }
        return new FeedbackModel();
      }
      return null;
    }, [hasFinalAnswer, turn.feedback, $auth.activeUser.id]);

    const [feedbackText, setFeedbackText] = useState(feedbackModel?.get('user_comment'));

    const handleThumbsUpClick = () => {
      if (feedbackModel.get('user_rating') === 1) {
        return;
      }
      setShowThankYouMessage(true);
      feedbackModel.save(
        { user_rating: 1, agent_turn_id: turn.id },
        { sendProvidedValuesOnly: true, toast: false, optimistic: true }
      );
    };

    const handleThumbsDownClick = () => {
      if (feedbackModel.get('user_rating') === -1) {
        setShowFeedbackPopover(true);
        return;
      }
      setShowThankYouMessage(true);
      setShowFeedbackPopover(true);
      feedbackModel.save(
        { user_rating: -1, agent_turn_id: turn.id },
        { sendProvidedValuesOnly: true, toast: false, optimistic: true }
      );
    };

    const handleFeedbackSubmit = () => {
      // Submit the feedback with optional text
      setShowFeedbackPopover(false);
      setShowThankYouMessage(true);
      feedbackModel.save(
        { user_comment: String(feedbackText).trim().substring(0, 5000), agent_turn_id: turn.id },
        { sendProvidedValuesOnly: false, toast: false, optimistic: true }
      );
    };

    const handlePopoverClose = () => {
      setShowFeedbackPopover(false);
      // setFeedbackText('');
    };

    // Hide thank you message after 3 seconds
    useEffect(() => {
      if (showThankYouMessage) {
        const timer = setTimeout(() => {
          setShowThankYouMessage(false);
        }, 3000);

        return () => clearTimeout(timer);
      }
      return undefined;
    }, [showThankYouMessage]);

    const isFinalAnswer =
      llm_response?.final_answer && step.processing_status === 'COMPLETED' && step.parent_step_id === null;

    return (
      <StepContainer>
        {llm_response?.reasoning && !hasSubAgentCalls && (
          <Flex alignItems="center" gap={1}>
            <Button
              small
              minimal
              color="muted"
              icon={isStepReasoningOpen ? 'caret-up' : 'caret-down'}
              style={{ alignSelf: 'flex-start' }}
              text={isStepReasoningOpen ? 'Hide reasoning' : 'Show reasoning'}
              onClick={() => onToggleReasoning(stepId)}
            />
          </Flex>
        )}
        <Flex flexDirection="column" gap={1}>
          {llm_response?.reasoning && !hasSubAgentCalls && (
            <Collapse isOpen={isStepReasoningOpen} title="Reasoning" defaultOpen>
              <ReasoningDetails>
                <ReasoningContent>
                  <Markdown>{llm_response.reasoning}</Markdown>
                </ReasoningContent>
              </ReasoningDetails>
            </Collapse>
          )}

          {toolCalls?.length > 0 && (
            <FlexColumn flexDirection="column" gap="8px">
              {(() => {
                // Group tool calls: sub-agent calls grouped together, regular calls rendered individually
                const groups = [];
                let currentSubAgent = null;
                let currentSubAgentCalls = [];

                toolCalls.forEach((toolCall) => {
                  const isSubAgent = toolCall.agent_name && toolCall.agent_name !== 'kaizen';

                  if (isSubAgent) {
                    if (currentSubAgent === toolCall.agent_name) {
                      currentSubAgentCalls.push(toolCall);
                    } else {
                      // Flush previous sub-agent group
                      if (currentSubAgent) {
                        groups.push({ type: 'subagent', agentName: currentSubAgent, calls: currentSubAgentCalls });
                      }
                      currentSubAgent = toolCall.agent_name;
                      currentSubAgentCalls = [toolCall];
                    }
                  } else {
                    // Flush any pending sub-agent group
                    if (currentSubAgent) {
                      groups.push({ type: 'subagent', agentName: currentSubAgent, calls: currentSubAgentCalls });
                      currentSubAgent = null;
                      currentSubAgentCalls = [];
                    }
                    groups.push({ type: 'regular', call: toolCall });
                  }
                });

                // Flush trailing sub-agent group
                if (currentSubAgent) {
                  groups.push({ type: 'subagent', agentName: currentSubAgent, calls: currentSubAgentCalls });
                }

                return groups.map((group) => {
                  if (group.type === 'regular') {
                    return (
                      <KaizenToolResult
                        key={group.call.id || group.call.tool_tracking_id}
                        tool={group.call}
                        step={step}
                        onSelectToolResult={onSelectToolResult}
                        onSendToExplorer={onSendToExplorer}
                        isCopilotMode={isCopilotMode}
                        isExistingChatLoading={isExistingChatLoading}
                        sessionModel={sessionModel}
                      />
                    );
                  }

                  return (
                    <SubAgentGroup
                      key={`subagent-${group.agentName}-${group.calls[0]?.tool_tracking_id || group.agentName}`}
                      agentName={group.agentName}
                      toolCalls={group.calls}
                      step={step}
                      reasoning={llm_response?.reasoning}
                      onSelectToolResult={onSelectToolResult}
                    />
                  );
                });
              })()}
            </FlexColumn>
          )}

          {isFinalAnswer && (
            <Flex flexDirection="column" gap={1}>
              <Markdown>{llm_response.final_answer}</Markdown>
              <Flex alignItems="center" gap={1}>
                <Tooltip content="Copy response" position="bottom">
                  <CopyToClipboard intent="default" text={llm_response.final_answer}>
                    <Button minimal icon={FiCopy} />
                  </CopyToClipboard>
                </Tooltip>

                <Button
                  minimal
                  icon={FiThumbsUp}
                  intent={feedbackModel.get('user_rating') === 1 ? 'success' : 'default'}
                  onClick={() => handleThumbsUpClick()}
                />

                <Popover
                  isOpen={showFeedbackPopover}
                  onClose={handlePopoverClose}
                  position="top"
                  minimal={false}
                  content={
                    <Flex flexDirection="column" gap={1} p={2} style={{ width: '330px', height: 'auto' }}>
                      <Text fontWeight="bold">Help us improve AI Advisor!</Text>
                      <Text color="muted" fontSize={13}>
                        Tell us what went wrong
                      </Text>
                      <TextArea
                        placeholder="Your feedback..."
                        value={feedbackText}
                        rows={2}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        style={{ resize: 'none' }}
                        autoFocus
                      />
                      <Flex justifyContent="flex-end" gap={1}>
                        <Button minimal text="Cancel" onClick={handlePopoverClose} />
                        <Button intent="primary" text="Submit" onClick={handleFeedbackSubmit} />
                      </Flex>
                    </Flex>
                  }
                >
                  <Button
                    minimal
                    icon={FiThumbsDown}
                    intent={feedbackModel.get('user_rating') === -1 ? 'danger' : 'default'}
                    onClick={() => handleThumbsDownClick('down')}
                  />
                </Popover>

                {showThankYouMessage && (
                  <Text fontSize={13} color="muted">
                    Thank you! Your feedback helps make AI Advisor better for everyone.
                  </Text>
                )}
              </Flex>
            </Flex>
          )}

          {step.error_message && step.error_message !== 'SCHEMA_FAILED' && (
            <Callout intent="danger">
              <Text small>Error: something went wrong</Text>
            </Callout>
          )}
        </Flex>
      </StepContainer>
    );
  }
);

StepComponent.displayName = 'StepComponent';

export default StepComponent;
